/** Every key a Snapshot reading can be emitted under, in one list. */
export const SNAPSHOT_READING_KEYS = [
    "sleep_hours",
    "device_score",
    "deep",
    "rem",
    "light",
    "awake",
    "efficiency",
    "wake_drift",
    "hrv",
    "rhr",
    "stress_time",
    "stress_load",
    "stress",
    "skin_temp",
    "breathing",
    "spo2",
    "steps",
    "trained",
    "active_min",
    "vigorous",
    "moderate",
    "low",
    "active_kcal",
    "sedentary",
    "weight",
    "bmi",
    "body_fat",
    "vo2",
    "systolic",
    "diastolic",
    "glucose",
    "mean_7d",
    "air_quality",
    "pollen",
    "uv",
    "pressure",
    "daylight",
    "warmest_night",
    "warm_nights",
    "warmest_day",
    "sunset",
];
/**
 * Plain-language definitions for the readings a drill-in shows, keyed by the
 * reading's own key. Partial on purpose: "Weight" and "Steps" are not jargon
 * and need no tooltip. The key type is not optional, so a definition can never
 * be written under a name no reading is emitted under.
 */
export const SNAPSHOT_GLOSSARY = {
    sleep_hours: "The hours you were actually asleep, not the hours you were in bed. Most adults settle between 7 and 9.",
    deep: "The deepest stage of sleep, when the body does most of its physical repair. It comes mostly in the first half of the night.",
    rhr: "Your heart rate at complete rest, in beats per minute. Lower than your usual tends to mean you are recovered; a rise of five or more often shows up before you feel run down.",
    stress: "Your device's estimate of how hard your body is working when you are not moving, from your heart rate and how it varies. Lower is calmer.",
    steps: "Every step counted through the day, however it was taken. The day's total only means what it says once the day is done.",
    trained: "Minutes in a workout your device recorded as a session, whatever it was. An hour of resistance work moves almost no steps, so this is the only place it shows.",
    active_min: "Minutes spent moving hard enough to raise your breathing. Guidelines ask for about 150 a week, which is a little over 20 a day.",
    vigorous: "Minutes of hard effort, where holding a conversation would be difficult. These count double toward a weekly total.",
    hrv: "Heart rate variability: the small gaps in time between your heartbeats, measured in milliseconds (ms). Higher than your usual usually means you are well rested.",
    rem: "Rapid eye movement sleep, the stage when most dreaming happens. It supports memory and mood.",
    efficiency: "The share of your time in bed that you spent asleep.",
    skin_temp: "How far your overnight skin temperature sat from your own usual. A sustained rise can flag strain or oncoming illness.",
    vo2: "The most oxygen your body can use in a minute of hard effort. A fitness measure: higher is fitter, and it climbs slowly.",
    bmi: "Body Mass Index: your weight against your height. It says nothing about muscle, so it reads high for people who carry a lot of it.",
    mean_7d: "The average of the last seven days, so one unusual day does not move the line on its own.",
    air_quality: "The Air Quality Index, a 0 to 500 scale for how much pollution is in the air. Under 50 is clean; over 100 starts to affect sensitive people.",
    pollen: "How much pollen was in the air, on the forecaster's own low to very high scale.",
    uv: "The UV index, a 0 to 11+ scale for how strong the sun's ultraviolet is. Over 3 is when skin starts to burn.",
    pressure: "How far the air pressure moved across the week. Large swings track with weather fronts, and some people feel them.",
    daylight: "The hours between sunrise and sunset, which set the light your body clock reads.",
    warmest_night: "The highest overnight low of the week. Deep sleep falls away in rooms much above 19 degrees.",
    warm_nights: "How many nights this week did not drop below the temperature deep sleep prefers.",
    warmest_day: "The highest temperature the week reached where you are.",
    spo2: "Blood oxygen: the share of your red blood cells carrying oxygen, as a percentage. It normally sits in the high 90s overnight and barely moves.",
    breathing: "How many breaths you take a minute while asleep. Most adults sit between 12 and 20, and your own figure is steadier than the range suggests.",
    stress_time: "How much of the day your device read your body as working hard while you were not moving, from your heart rate and how it varies.",
    wake_drift: "How far your wake time moved from the time you usually wake. Your body clock reads the wake time, not the bedtime.",
    body_fat: "The share of your weight that is fat rather than muscle, bone and water. Scales estimate it from a small current, so read the trend and not the day.",
    glucose: "The sugar in your blood, in mmol/L. It rises after eating and settles back within a couple of hours.",
    systolic: "The top blood pressure number: the push against your artery walls as your heart beats. Under 120 is the usual target.",
    diastolic: "The bottom blood pressure number: the push against your artery walls between beats. Under 80 is the usual target.",
    active_kcal: "The calories you burned moving, on top of what your body uses at rest.",
    sedentary: "The hours your device read as sitting or lying still while you were awake.",
    light: "The lightest stage of sleep, and most of the night for most people. It is where you drift in and out, and it still counts.",
    awake: "The time you spent awake after first falling asleep, not the time before it.",
    low: "Time spent moving gently: walking about, standing, moving around the house.",
    moderate: "Time spent moving hard enough to breathe faster but still hold a conversation.",
    device_score: "Your device's own overall read of the night, on its own scale. It is their formula, not ours, so it is here for continuity rather than as a verdict.",
    sunset: "When the sun set on the most recent day. Light in the evening pushes your body clock later.",
};
export function snapshotDefinition(key) {
    if (!key)
        return null;
    return SNAPSHOT_GLOSSARY[key] ?? null;
}
//# sourceMappingURL=snapshot-glossary.js.map